import { useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useGetPostersQuery } from '@/features/editor/videosApi'

export type VideoOrientation = 'portrait' | 'landscape'

/** Уже измеренные ролики: при возврате на блок экран сразу нужной ширины. */
const known = new Map<string, VideoOrientation>()

/** Обложка появляется только после обработки ролика: без опроса её не дождаться. */
const POSTERS_POLL_MS = 5000

/** Обложки нет и через столько — опрос прекращается: ролик, видимо, не обработается. */
export const POSTERS_POLL_MAX_MS = 600_000

/**
 * Ориентация ролика по его кадру-обложке (Kinescope снимает её с самого видео):
 * поля про пропорции у ролика в нашем API нет, а размеры кадра — есть. Пока
 * обложки нет (ролик обрабатывается) — `portrait`, как привычный экран телефона,
 * и обложка опрашивается: иначе кнопка поворота появлялась только после перезагрузки.
 */
export const useVideoOrientation = (videoId: string | null): VideoOrientation => {
  const [pollingInterval, setPollingInterval] = useState(POSTERS_POLL_MS)
  /** Ролик, по которому срок ожидания вышел: смена `videoId` снимает отметку сама. */
  const [expiredFor, setExpiredFor] = useState<string | null>(null)
  const { data: posters, error } = useGetPostersQuery(videoId ?? skipToken, { pollingInterval })
  /**
   * 404 — «ещё нет»: ручка отвечает им, пока ролик не сохранён в блоке, а превью рисует
   * черновик, где `videoId` уже стоит. Опрос идёт дальше (до `POSTERS_POLL_MAX_MS`);
   * любая другая ошибка его обрывает.
   */
  const failed = !!error && (error as { status?: unknown }).status !== 404
  const poster = posters?.find((p) => p.active) ?? posters?.[0]
  const src = poster ? poster.sm || poster.md || poster.original : null
  const waiting = !src && !(videoId && known.has(videoId))
  const expired = videoId !== null && expiredFor === videoId

  useEffect(() => {
    if (!waiting || !videoId) return
    const timer = setTimeout(() => setExpiredFor(videoId), POSTERS_POLL_MAX_MS)
    return () => clearTimeout(timer)
  }, [waiting, videoId])

  useEffect(
    () => setPollingInterval(waiting && !expired && !failed ? POSTERS_POLL_MS : 0),
    [waiting, expired, failed],
  )
  const [measured, setMeasured] = useState<VideoOrientation | null>(null)

  useEffect(() => {
    if (!videoId || !src || known.has(videoId)) return
    const img = new Image()
    img.onload = () => {
      const o: VideoOrientation = img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait'
      known.set(videoId, o)
      setMeasured(o)
    }
    img.src = src
  }, [videoId, src])

  if (!videoId) return 'portrait'
  return known.get(videoId) ?? measured ?? 'portrait'
}
